// Nether wastes generator (GEN-10, M7). PURE + deterministic: same seed + config
// yields identical nether chunks on the main thread, in the gen worker, and on the
// server. The nether occupies world Y 0..127 (NETHER.ROOF_Y): a bedrock sandwich
// (solid at 0 and 127, roughened bands just inside), a netherrack body shaped by
// a 2D floor/ceiling field and carved by 3D cavern noise, a lava sea at LAVA_SEA,
// glowstone blobs hanging from the ceiling, and nether-quartz veins in the rock.
import { WORLD, NETHER, NETHER_BIOME } from '../../config';
import { STATE_AIR } from '../../engine/section';
import { Rng, defineSalt } from '../../engine/rng';
import { Simplex, fbm2, fbm3 } from '../../engine/noise';
import { NetherBiomeSampler, NETHER_SURFACES, NETHER_FLORA, type NetherBiome } from './biomes';
import { VINES } from '../../game/vines';

/** Block-state ids the nether generator places (soul_sand/magma are their own
 *  later-biome features; the wastes place these). */
export interface NetherIds {
  bedrock: number;
  netherrack: number;
  lava: number;
  glowstone: number;
  quartzOre: number;
  magma: number;
  /** Nether gold ore + ancient debris. Optional so a registry that predates them
   *  generates the M7 nether byte-for-byte (the same opt-in the biome layer uses). */
  netherGoldOre?: number;
  ancientDebris?: number;
}

/** Resolve a block name → state id, for the biome surface pass. Absent ⇒ the
 *  dimension generates as plain wastes (every world before nether biomes). */
export type NetherStateOf = (name: string) => number | undefined;

const S_FLOOR = defineSalt('nether-floor');
const S_CEIL = defineSalt('nether-ceiling');
const S_CAVE = defineSalt('nether-cave');
const S_FEATURE = defineSalt('nether-feature');
const S_BEDROCK = defineSalt('nether-bedrock');
const S_DELTA = defineSalt('nether-delta');

/** A place-cell sink (local x/z 0..15, world Y). */
export type SetCell = (lx: number, y: number, lz: number, state: number) => void;
/** A read-cell accessor (local x/z 0..15, world Y). */
export type GetCell = (lx: number, y: number, lz: number) => number;

export class NetherTerrain {
  private readonly floorNoise: Simplex;
  private readonly ceilNoise: Simplex;
  private readonly caveNoise: Simplex;
  private readonly featureRng: Rng;
  private readonly bedrockSalt: number;

  private readonly biomes: NetherBiomeSampler;
  /** Per-biome resolved surface state ids, empty when no resolver was supplied. */
  private readonly surfaceIds: Partial<Record<NetherBiome, { top?: number; filler?: number; depth: number }>> = {};
  /** Per-biome resolved flora state ids (fungal forests only). `vine`/`sprouts`
   *  stay undefined when the registry predates them, so an older world's forests
   *  generate exactly as they did — the same opt-in the whole biome layer uses. */
  private readonly floraIds: Partial<Record<NetherBiome, {
    stem: number; wart: number; fungus: number; roots: number;
    vine?: number; vineDir?: 1 | -1; sprouts?: number;
  }>> = {};
  private readonly deltaRng: Rng;
  /** Shroomlight, when the registry has it (fungal canopies stud with it). */
  private readonly shroomlightState?: number;

  constructor(seed: number | string | bigint, private readonly ids: NetherIds, stateOf?: NetherStateOf) {
    // Salted noise streams so floor/ceiling/caves are independent (ENG-3, E-25).
    this.floorNoise = new Simplex(`${seed}#${S_FLOOR}`);
    this.ceilNoise = new Simplex(`${seed}#${S_CEIL}`);
    this.caveNoise = new Simplex(`${seed}#${S_CAVE}`);
    this.featureRng = new Rng(seed).fork(S_FEATURE);
    // Deterministic bedrock-roughness salt (mixes into the position hash).
    this.bedrockSalt = new Rng(`${seed}#${S_BEDROCK}`).nextU32();
    this.biomes = new NetherBiomeSampler(seed);
    this.deltaRng = new Rng(seed).fork(S_DELTA);
    this.shroomlightState = stateOf?.('shroomlight');
    if (stateOf) {
      for (const [biome, surf] of Object.entries(NETHER_SURFACES)) {
        const top = surf.top ? stateOf(surf.top) : undefined;
        const filler = surf.filler ? stateOf(surf.filler) : undefined;
        if (top !== undefined || filler !== undefined) {
          this.surfaceIds[biome as NetherBiome] = { top, filler, depth: surf.depth };
        }
      }
      for (const [biome, flora] of Object.entries(NETHER_FLORA)) {
        const stem = stateOf(flora.stem), wart = stateOf(flora.wart);
        const fungus = stateOf(flora.fungus), roots = stateOf(flora.roots);
        if (stem !== undefined && wart !== undefined && fungus !== undefined && roots !== undefined) {
          const vine = flora.vine ? stateOf(flora.vine) : undefined;
          this.floraIds[biome as NetherBiome] = {
            stem, wart, fungus, roots,
            vine,
            vineDir: vine !== undefined && flora.vine ? VINES[flora.vine]?.dir : undefined,
            sprouts: flora.sprouts ? stateOf(flora.sprouts) : undefined,
          };
        }
      }
    }
  }

  /** Nether biome at a column (the host reports it for `/locate`-style queries). */
  biomeAt(x: number, z: number): NetherBiome {
    return this.biomes.biomeAt(x, z);
  }

  /** Netherrack floor top at world (x,z). */
  private floorTop(x: number, z: number): number {
    return Math.round(NETHER.FLOOR_BASE + fbm2(this.floorNoise, x, z, { frequency: NETHER.FLOOR_FREQ }) * NETHER.FLOOR_AMP);
  }

  /** Public floor top (spawn-placement surface height, before carving). */
  floorTopAt(x: number, z: number): number {
    return this.floorTop(x, z);
  }

  /** Netherrack ceiling bottom at world (x,z). */
  private ceilBottom(x: number, z: number): number {
    return Math.round(NETHER.CEIL_BASE - fbm2(this.ceilNoise, x, z, { frequency: NETHER.CEIL_FREQ }) * NETHER.CEIL_AMP);
  }

  /** Deterministic hash in [0,1) for the bedrock-band roughness. */
  private hash(x: number, y: number, z: number): number {
    let h = (this.bedrockSalt ^ Math.imul(x, 0x85ebca6b) ^ Math.imul(y, 0xc2b2ae35) ^ Math.imul(z, 0x27d4eb2f)) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
    h = Math.imul(h ^ (h >>> 13), 0x297a2d39) >>> 0;
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
  }

  /** True when the cell in a bedrock band should be solid bedrock (vs netherrack). */
  private isBedrock(x: number, y: number, z: number): boolean {
    if (y === 0 || y === NETHER.ROOF_Y) return true;
    if (y >= 1 && y <= NETHER.BEDROCK_FLOOR_TOP) {
      return this.hash(x, y, z) < (NETHER.BEDROCK_FLOOR_TOP + 1 - y) / (NETHER.BEDROCK_FLOOR_TOP + 1);
    }
    if (y >= NETHER.BEDROCK_ROOF_BOTTOM && y < NETHER.ROOF_Y) {
      return this.hash(x, y, z) < (y - (NETHER.BEDROCK_ROOF_BOTTOM - 1)) / (NETHER.ROOF_Y - NETHER.BEDROCK_ROOF_BOTTOM + 1);
    }
    return false;
  }

  /** Base column state (bedrock/netherrack/lava/air) at a cell, before features. */
  private baseState(x: number, y: number, z: number, floorTop: number, ceilBottom: number): number {
    if (y < 0 || y > NETHER.ROOF_Y) return STATE_AIR;
    const inFloorBand = y <= NETHER.BEDROCK_FLOOR_TOP;
    const inRoofBand = y >= NETHER.BEDROCK_ROOF_BOTTOM;
    if (inFloorBand || inRoofBand) {
      return this.isBedrock(x, y, z) ? this.ids.bedrock : this.ids.netherrack;
    }
    const solid = y <= floorTop || y >= ceilBottom;
    if (solid) {
      const carved = fbm3(this.caveNoise, x, y, z, { frequency: NETHER.CAVE_FREQ, octaves: 2 }) > NETHER.CAVE_THRESH;
      if (!carved) return this.ids.netherrack;
    }
    return y <= NETHER.LAVA_SEA ? this.ids.lava : STATE_AIR;
  }

  /** Fill one nether chunk column-by-column, then decorate (glowstone + quartz). */
  fill(cx: number, cz: number, get: GetCell, set: SetCell): void {
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    for (let lz = 0; lz < WORLD.SECTION; lz++) {
      for (let lx = 0; lx < WORLD.SECTION; lx++) {
        const wx = bx + lx, wz = bz + lz;
        const floorTop = this.floorTop(wx, wz);
        const ceilBottom = this.ceilBottom(wx, wz);
        for (let y = 0; y <= NETHER.ROOF_Y; y++) {
          const state = this.baseState(wx, y, wz, floorTop, ceilBottom);
          if (state !== STATE_AIR) set(lx, y, lz, state);
        }
      }
    }
    this.decorate(cx, cz, get, set);
    this.paintBiomes(cx, cz, get, set);
  }

  /**
   * Repaint each column's exposed floor in its biome's materials. Runs after the
   * body + decoration passes so it sees the carved cavern surface, and only ever
   * *replaces* netherrack — glowstone, quartz ore, magma and lava all survive. A
   * world whose generator was built without a state resolver skips this entirely
   * and generates as plain wastes (parity with every nether before this).
   */
  private paintBiomes(cx: number, cz: number, get: GetCell, set: SetCell): void {
    if (Object.keys(this.surfaceIds).length === 0) return;
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    const rng = this.deltaRng.forkAt(S_DELTA, cx, cz);
    const top = NETHER.BEDROCK_ROOF_BOTTOM - NETHER_BIOME.SURFACE_SCAN_MARGIN;
    for (let lz = 0; lz < WORLD.SECTION; lz++) {
      for (let lx = 0; lx < WORLD.SECTION; lx++) {
        const surf = this.surfaceIds[this.biomes.biomeAt(bx + lx, bz + lz)];
        if (!surf) continue;
        // The floor: topmost netherrack under open air, above the lava sea.
        let fy = -1;
        for (let y = top; y > NETHER.LAVA_SEA; y--) {
          if (get(lx, y, lz) === this.ids.netherrack && get(lx, y + 1, lz) === STATE_AIR) { fy = y; break; }
        }
        if (fy < 0) continue;
        if (surf.top !== undefined) set(lx, fy, lz, surf.top);
        if (surf.filler !== undefined) {
          for (let d = 1; d <= surf.depth; d++) {
            const y = fy - d;
            if (y <= NETHER.BEDROCK_FLOOR_TOP) break;
            if (get(lx, y, lz) === this.ids.netherrack) set(lx, y, lz, surf.filler);
          }
        }
      }
    }
    this.deltaPillars(cx, cz, get, set, rng);
    this.growFungalForest(cx, cz, get, set, rng);
  }

  /** Fungal forests: huge fungi (a stem column under a wart-block canopy studded
   *  with shroomlight) plus small fungi and roots carpeting the nylium. */
  private growFungalForest(cx: number, cz: number, get: GetCell, set: SetCell, rng: Rng): void {
    if (Object.keys(this.floraIds).length === 0) return;
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    /** Nylium top of a column (the repainted floor), or −1. */
    const nyliumTop = (lx: number, lz: number, cap: number): number => {
      for (let y = NETHER.BEDROCK_ROOF_BOTTOM - 1; y > NETHER.LAVA_SEA; y--) {
        if (get(lx, y, lz) === cap && get(lx, y + 1, lz) === STATE_AIR) return y;
      }
      return -1;
    };
    for (let t = 0; t < NETHER_BIOME.FUNGUS_TRIES; t++) {
      const lx = rng.nextInt(WORLD.SECTION), lz = rng.nextInt(WORLD.SECTION);
      const biome = this.biomes.biomeAt(bx + lx, bz + lz);
      const flora = this.floraIds[biome];
      const cap = this.surfaceIds[biome]?.top;
      if (!flora || cap === undefined) continue;
      const fy = nyliumTop(lx, lz, cap);
      if (fy < 0) continue;
      if (rng.nextInt(NETHER_BIOME.HUGE_FUNGUS_ONE_IN) === 0) {
        this.hugeFungus(lx, fy, lz, flora, get, set, rng);
      } else if (flora.vineDir === 1 && flora.vine !== undefined && rng.nextInt(4) === 0) {
        // A twisting vine climbs straight up out of the warped nylium.
        this.hangVine(lx, fy + 1, lz, 1, flora.vine, get, set, rng);
      } else {
        // Ground cover: a small fungus, a spray of roots, or — where the biome has
        // them — the warped forest's teal sprouts.
        const roll = rng.nextInt(6);
        const cover = roll === 0 ? flora.fungus
          : roll <= 2 && flora.sprouts !== undefined ? flora.sprouts
            : flora.roots;
        set(lx, fy + 1, lz, cover);
      }
    }
    this.hangCeilingVines(cx, cz, get, set, rng);
  }

  /**
   * Weeping vines dangling from the rocky ceiling over a crimson forest — the
   * dimension's roof is netherrack, which is a weeping-vine anchor, so the strands
   * hang in open cavern air rather than only off a canopy.
   */
  private hangCeilingVines(cx: number, cz: number, get: GetCell, set: SetCell, rng: Rng): void {
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    for (let t = 0; t < NETHER_BIOME.CEILING_VINE_TRIES; t++) {
      const lx = rng.nextInt(WORLD.SECTION), lz = rng.nextInt(WORLD.SECTION);
      const flora = this.floraIds[this.biomes.biomeAt(bx + lx, bz + lz)];
      if (!flora || flora.vine === undefined || flora.vineDir !== -1) continue;
      // The first air cell under the rocky ceiling.
      let cy = -1;
      for (let y = NETHER.BEDROCK_ROOF_BOTTOM - 1; y > NETHER.LAVA_SEA; y--) {
        if (get(lx, y, lz) === STATE_AIR && get(lx, y + 1, lz) === this.ids.netherrack) { cy = y; break; }
      }
      if (cy < 0) continue;
      this.hangVine(lx, cy, lz, -1, flora.vine, get, set, rng);
    }
  }

  /** Run a vine strand from (lx,y0,lz) in `dir`, stopping at the first blockage. */
  private hangVine(lx: number, y0: number, lz: number, dir: 1 | -1, vine: number, get: GetCell, set: SetCell, rng: Rng): void {
    const len = NETHER_BIOME.VINE_MIN_LEN + rng.nextInt(NETHER_BIOME.VINE_MAX_LEN - NETHER_BIOME.VINE_MIN_LEN + 1);
    for (let i = 0; i < len; i++) {
      const y = y0 + dir * i;
      if (y <= NETHER.LAVA_SEA || y >= NETHER.BEDROCK_ROOF_BOTTOM) return;
      if (get(lx, y, lz) !== STATE_AIR) return;
      set(lx, y, lz, vine);
    }
  }

  /** One huge fungus rooted on the nylium at (lx,fy,lz): stem, canopy, pores. */
  private hugeFungus(
    lx: number, fy: number, lz: number,
    flora: { stem: number; wart: number; vine?: number; vineDir?: 1 | -1 },
    get: GetCell, set: SetCell, rng: Rng,
  ): void {
    const h = NETHER_BIOME.FUNGUS_MIN_H + rng.nextInt(NETHER_BIOME.FUNGUS_MAX_H - NETHER_BIOME.FUNGUS_MIN_H + 1);
    const topY = fy + h;
    if (topY + 1 >= NETHER.BEDROCK_ROOF_BOTTOM) return;
    for (let y = fy + 1; y <= topY; y++) {
      if (get(lx, y, lz) !== STATE_AIR) return; // blocked — leave the column alone
      set(lx, y, lz, flora.stem);
    }
    // Canopy: a wart-block plus around the stem top, with shroomlight pores.
    const shroom = this.shroomlightState ?? flora.wart;
    for (let dy = 0; dy <= 1; dy++) {
      const r = dy === 0 ? 1 : 0;
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          const x = lx + dx, z = lz + dz, y = topY + dy;
          if (x < 0 || x >= WORLD.SECTION || z < 0 || z >= WORLD.SECTION) continue;
          if (get(x, y, z) !== STATE_AIR && !(dx === 0 && dz === 0)) continue;
          const lit = dy === 0 && (dx !== 0 || dz !== 0) && rng.nextInt(4) === 0;
          set(x, y, z, lit ? shroom : flora.wart);
        }
      }
    }
    // Weeping vines hang off the canopy rim (crimson only — a warped fungus's vine
    // climbs, so it has no business dangling from the underside of anything).
    if (flora.vine === undefined || flora.vineDir !== -1) return;
    for (let t = 0; t < NETHER_BIOME.CANOPY_VINE_TRIES; t++) {
      const dx = rng.nextInt(3) - 1, dz = rng.nextInt(3) - 1;
      if (dx === 0 && dz === 0) continue; // never under the stem itself
      const x = lx + dx, z = lz + dz;
      if (x < 0 || x >= WORLD.SECTION || z < 0 || z >= WORLD.SECTION) continue;
      if (get(x, topY, z) !== flora.wart && get(x, topY, z) !== shroom) continue;
      this.hangVine(x, topY - 1, z, -1, flora.vine, get, set, rng);
    }
  }

  /** Basalt deltas' signature: short basalt columns jutting off the delta floor. */
  private deltaPillars(cx: number, cz: number, get: GetCell, set: SetCell, rng: Rng): void {
    const basalt = this.surfaceIds.basalt_deltas?.top;
    if (basalt === undefined) return;
    const bx = cx * WORLD.SECTION, bz = cz * WORLD.SECTION;
    for (let t = 0; t < NETHER_BIOME.DELTA_PILLAR_TRIES; t++) {
      const lx = rng.nextInt(WORLD.SECTION), lz = rng.nextInt(WORLD.SECTION);
      const h = 1 + rng.nextInt(NETHER_BIOME.DELTA_PILLAR_MAX);
      if (this.biomes.biomeAt(bx + lx, bz + lz) !== 'basalt_deltas') continue;
      // Find the delta floor (already repainted to basalt) and stack on it.
      let fy = -1;
      for (let y = NETHER.BEDROCK_ROOF_BOTTOM - 1; y > NETHER.LAVA_SEA; y--) {
        if (get(lx, y, lz) === basalt && get(lx, y + 1, lz) === STATE_AIR) { fy = y; break; }
      }
      if (fy < 0) continue;
      for (let i = 1; i <= h; i++) {
        if (fy + i >= NETHER.BEDROCK_ROOF_BOTTOM) break;
        if (get(lx, fy + i, lz) !== STATE_AIR) break;
        set(lx, fy + i, lz, basalt);
      }
    }
  }

  /** Glowstone blobs on the ceiling underside + nether-quartz veins in the rock. */
  private decorate(cx: number, cz: number, get: GetCell, set: SetCell): void {
    const rng = this.featureRng.forkAt(S_FEATURE, cx, cz);
    const isRock = (lx: number, y: number, lz: number): boolean => get(lx, y, lz) === this.ids.netherrack;
    // Glowstone: a small cluster hanging in the air just below netherrack ceiling.
    for (let t = 0; t < NETHER.GLOWSTONE_TRIES; t++) {
      const lx = rng.nextInt(WORLD.SECTION), lz = rng.nextInt(WORLD.SECTION);
      // Scan down from the roof for the first air cell under a netherrack ceiling.
      let cy = -1;
      for (let y = NETHER.BEDROCK_ROOF_BOTTOM - 1; y > NETHER.LAVA_SEA; y--) {
        if (get(lx, y, lz) === STATE_AIR && isRock(lx, y + 1, lz)) { cy = y; break; }
      }
      if (cy < 0) continue;
      const blob = 2 + rng.nextInt(3);
      for (let i = 0; i < blob; i++) {
        const ox = lx + rng.nextInt(3) - 1, oz = lz + rng.nextInt(3) - 1, oy = cy - rng.nextInt(2);
        if (ox < 0 || ox >= WORLD.SECTION || oz < 0 || oz >= WORLD.SECTION) continue;
        if (oy > NETHER.LAVA_SEA && get(ox, oy, oz) === STATE_AIR) set(ox, oy, oz, this.ids.glowstone);
      }
    }
    // Nether-quartz veins — only replace netherrack (self-gating like overworld ores).
    for (let t = 0; t < NETHER.QUARTZ_TRIES; t++) {
      const lx = rng.nextInt(WORLD.SECTION), lz = rng.nextInt(WORLD.SECTION);
      const y = NETHER.BEDROCK_FLOOR_TOP + 1 + rng.nextInt(NETHER.BEDROCK_ROOF_BOTTOM - NETHER.BEDROCK_FLOOR_TOP - 2);
      for (let v = 0; v < NETHER.QUARTZ_VEIN; v++) {
        const ox = lx + rng.nextInt(3) - 1, oz = lz + rng.nextInt(3) - 1, oy = y + rng.nextInt(3) - 1;
        if (ox < 0 || ox >= WORLD.SECTION || oz < 0 || oz >= WORLD.SECTION) continue;
        if (isRock(ox, oy, oz)) set(ox, oy, oz, this.ids.quartzOre);
      }
    }
    // Magma blocks: netherrack cells whose top sits at the lava-sea surface.
    for (let lz = 0; lz < WORLD.SECTION; lz++) {
      for (let lx = 0; lx < WORLD.SECTION; lx++) {
        const y = NETHER.LAVA_SEA;
        if (isRock(lx, y, lz) && get(lx, y + 1, lz) === STATE_AIR && rng.nextInt(6) === 0) {
          set(lx, y, lz, this.ids.magma);
        }
      }
    }
    // Nether gold + ancient debris draw AFTER everything above, so a world whose
    // registry lacks them generates an identical quartz/glowstone/magma nether.
    this.veinNetherGold(get, set, rng);
    this.veinAncientDebris(get, set, rng);
  }

  /** Nether-gold veins: quartz's rarer cousin, in the same rock, same band. */
  private veinNetherGold(get: GetCell, set: SetCell, rng: Rng): void {
    const ore = this.ids.netherGoldOre;
    if (ore === undefined) return;
    for (let t = 0; t < NETHER.NETHER_GOLD_TRIES; t++) {
      const lx = rng.nextInt(WORLD.SECTION), lz = rng.nextInt(WORLD.SECTION);
      const y = NETHER.BEDROCK_FLOOR_TOP + 1 + rng.nextInt(NETHER.BEDROCK_ROOF_BOTTOM - NETHER.BEDROCK_FLOOR_TOP - 2);
      for (let v = 0; v < NETHER.NETHER_GOLD_VEIN; v++) {
        const ox = lx + rng.nextInt(3) - 1, oz = lz + rng.nextInt(3) - 1, oy = y + rng.nextInt(3) - 1;
        if (ox < 0 || ox >= WORLD.SECTION || oz < 0 || oz >= WORLD.SECTION) continue;
        if (get(ox, oy, oz) === this.ids.netherrack) set(ox, oy, oz, ore);
      }
    }
  }

  /**
   * Ancient debris — the one ore Minecraft never lets you SEE. It only replaces
   * netherrack whose six neighbours are all solid, so it never faces a cavern
   * wall: you find it by mining blind, not by walking past it.
   *
   * A cell on the chunk boundary is rejected outright, because the neighbouring
   * chunk isn't visible here and "enclosed" can't be answered without it. Refusing
   * is the only answer that stays deterministic — a chunk must generate the same
   * whether or not its neighbour happens to be loaded (ENG-3).
   */
  private veinAncientDebris(get: GetCell, set: SetCell, rng: Rng): void {
    const debris = this.ids.ancientDebris;
    if (debris === undefined) return;
    const enclosed = (lx: number, y: number, lz: number): boolean => {
      if (lx <= 0 || lx >= WORLD.SECTION - 1 || lz <= 0 || lz >= WORLD.SECTION - 1) return false;
      if (get(lx, y, lz) !== this.ids.netherrack) return false;
      for (const [dx, dy, dz] of NEIGHBOURS) {
        if (get(lx + dx, y + dy, lz + dz) === STATE_AIR) return false;
      }
      return true;
    };
    for (let t = 0; t < NETHER.DEBRIS_TRIES; t++) {
      const lx = rng.nextInt(WORLD.SECTION), lz = rng.nextInt(WORLD.SECTION);
      const upper = rng.nextInt(NETHER.DEBRIS_UPPER_ONE_IN) === 0;
      const [lo, hi] = upper
        ? [NETHER.DEBRIS_UPPER_MIN, NETHER.DEBRIS_UPPER_MAX]
        : [NETHER.DEBRIS_MAIN_MIN, NETHER.DEBRIS_MAIN_MAX];
      const y = lo + rng.nextInt(hi - lo + 1);
      for (let v = 0; v < NETHER.DEBRIS_VEIN; v++) {
        const ox = lx + rng.nextInt(3) - 1, oz = lz + rng.nextInt(3) - 1, oy = y + rng.nextInt(3) - 1;
        if (enclosed(ox, oy, oz)) set(ox, oy, oz, debris);
      }
    }
  }
}

/** The six face neighbours (the debris enclosure test). */
const NEIGHBOURS: readonly (readonly [number, number, number])[] = [
  [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];
